import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Button } from '@monorepo/shared/componentsV2/Button';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { getErrorMessage, getFooterErrorMessage } from './errorHelpers';
import { FileUploadStatus, type UploadedFile } from './hooks/useUploadedFiles';
import {
  ErrorSymbol,
  FileListScrollContainer,
  FileName,
  FileNameAndStatusContainer,
  FileStatus,
  ModalContent,
  OutlinedFileContainer,
  SubContainer,
  SubContainerTitle,
} from './styled';

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledTitle = styled(SubContainerTitle)`
  font-size: 16px;
  font-weight: 500;
`;

const StyledParagraph = styled.p`
  font-size: 14px;
  text-align: center;
`;

const StyledButton = styled(Button)`
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.blue};

  &:hover {
    background-color: ${({ theme }) => theme.colors.blue};
    opacity: 0.9;
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.blue};
    opacity: 0.8;
  }
`;

const ErrorModalButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const StyledErrorFileStatus = styled(FileStatus)`
  color: ${({ theme }) => theme.colors.darkRed};
`;

type LogEntriesErrorOverlayProps = {
  uploadedFiles: Map<string, UploadedFile>;
  removeFileData: (requestId: string) => void;
  setShowErrorOverlay: React.Dispatch<React.SetStateAction<boolean>>;
};

const getUploadedFilesWithErrors = (
  uploadedFiles: Map<string, UploadedFile>,
): { requestId: string; file: UploadedFile }[] =>
  Array.from(uploadedFiles.entries())
    .filter(([, file]) => file.status === FileUploadStatus.Error)
    .map(([requestId, file]) => ({ requestId, file }));

export function LogEntriesErrorOverlay({
  uploadedFiles,
  removeFileData,
  setShowErrorOverlay,
}: LogEntriesErrorOverlayProps) {
  const uploadedFilesErrors = useMemo(
    () => getUploadedFilesWithErrors(uploadedFiles),
    [uploadedFiles],
  );

  const handleDismissErrorOverlay = useCallback(() => {
    uploadedFilesErrors.forEach(({ requestId }) => {
      removeFileData(requestId);
    });
    setShowErrorOverlay(false);
  }, [removeFileData, uploadedFilesErrors, setShowErrorOverlay]);

  return (
    <ModalContent>
      <SubContainer>
        <TitleContainer>
          <StyledTitle>
            {uploadedFilesErrors.length}{' '}
            {uploadedFilesErrors.length === 1 ? 'file was' : 'files were'} not
            able to be processed
          </StyledTitle>
        </TitleContainer>
        <FileListScrollContainer>
          {uploadedFilesErrors.map(({ requestId, file }) => (
            <OutlinedFileContainer key={requestId}>
              <FileNameAndStatusContainer>
                <FileName>{file.name}</FileName>
                <StyledErrorFileStatus>
                  {getErrorMessage(file.error)}
                </StyledErrorFileStatus>
              </FileNameAndStatusContainer>
              <ErrorSymbol>
                <ErrorOutlineIcon color="error" />
              </ErrorSymbol>
            </OutlinedFileContainer>
          ))}
        </FileListScrollContainer>
        <StyledParagraph>{getFooterErrorMessage()}</StyledParagraph>
        <ErrorModalButtonContainer>
          <StyledButton variant="contained" onClick={handleDismissErrorOverlay}>
            Re-upload Files
          </StyledButton>
        </ErrorModalButtonContainer>
      </SubContainer>
    </ModalContent>
  );
}
